import { Component } from '@theme/component';

/**
 * Collapses the membership comparison table behind a button on small screens.
 *
 * Progressive enhancement: the table is visible in the markup and the toggle is
 * an ordinary `<button>`, so with no JavaScript the full chart renders and
 * scrolls sideways exactly as it does on desktop. All this class adds is the
 * collapsed starting state on mobile and the open/closed switching.
 *
 * Desktop is driven by the same media query the stylesheet uses to hide the
 * button, so the panel can never be left hidden with no way to reopen it — a
 * resize past the breakpoint re-syncs both the panel and `aria-expanded`.
 *
 * @extends {Component<{
 *   toggle: HTMLButtonElement,
 *   panel: HTMLElement,
 * }>}
 */
class MembershipCompareComponent extends Component {
  /** Matches the breakpoint at which membership-compare.css hides the toggle. */
  static breakpoint = '(min-width: 750px)';

  /** @type {MediaQueryList | undefined} */
  #media;

  /** @type {AbortController | undefined} */
  #controller;

  /** Whether the merchant-facing panel is open while the toggle is in play. */
  #open = false;

  connectedCallback() {
    super.connectedCallback();

    const { toggle, panel } = this.refs;

    if (!(toggle instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    this.#media = matchMedia(MembershipCompareComponent.breakpoint);
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    this.#media.addEventListener('change', this.#sync, { signal });

    // A row or tier selected in the theme editor is worth revealing, otherwise
    // the merchant edits a block they cannot see.
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = undefined;
    this.#media = undefined;
  }

  /** Flips the panel. Bound declaratively from the button's `on:click`. */
  toggle() {
    this.#open = !this.#open;
    this.#sync();
  }

  /** Writes `#open` — or the always-open desktop state — onto the DOM. */
  #sync = () => {
    const { toggle, panel } = this.refs;

    if (!(toggle instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    const open = this.#media?.matches ? true : this.#open;

    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.classList.toggle('membership-compare__toggle--open', open);
  };

  /**
   * @param {Event} event - The `shopify:block:select` event; its target is the block element.
   */
  #handleBlockSelect = (event) => {
    const { target } = event;

    if (!(target instanceof HTMLElement) || !this.contains(target) || this.#open) return;

    this.#open = true;
    this.#sync();
  };
}

if (!customElements.get('membership-compare-component')) {
  customElements.define('membership-compare-component', MembershipCompareComponent);
}
